/**
 * Prism Workflow UI Controller
 */

import type {
  AppViewState,
  WorkforceAgentRow,
  WorkforceBindingRow,
  WorkforceConfig,
  WorkforceInsightsState,
  WorkforceRoleRow,
  WorkforceSimulateResultState,
  WorkforceUnitRow,
} from "../app-view-state.ts";
import { serializeConfigForm } from "./config/form-utils.ts";

let lastBudgetNotificationKey = "";

function maybeNotifyBudgetAlerts(alerts: WorkforceInsightsState["budgetAlerts"]): void {
  if (typeof window === "undefined" || alerts.length === 0) {
    return;
  }
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }
  const key = alerts.map((a) => `${a.agentId}:${a.percentUsed}`).join("|");
  if (key === lastBudgetNotificationKey) {
    return;
  }
  lastBudgetNotificationKey = key;
  try {
    new Notification(`Prism Workflow: ${alerts.length} budget alert(s)`, {
      body: alerts
        .slice(0, 4)
        .map((a) => `${a.name} (${a.percentUsed}% of cap)`)
        .join("; "),
    });
  } catch {
    /* ignore */
  }
}

function normalizeSimulatePayload(result: unknown): WorkforceSimulateResultState | null {
  if (!result || typeof result !== "object") {
    return null;
  }
  const r = result as Record<string, unknown>;
  const deniedRaw =
    r.denied && typeof r.denied === "object" ? (r.denied as Record<string, unknown>) : null;
  const decisionRaw =
    r.decision && typeof r.decision === "object" ? (r.decision as Record<string, unknown>) : null;
  return {
    success: r.success === true,
    agentId: typeof r.agentId === "string" ? r.agentId : undefined,
    decision:
      decisionRaw != null
        ? {
            confidence:
              typeof decisionRaw.confidence === "number" ? decisionRaw.confidence : undefined,
            reason: typeof decisionRaw.reason === "string" ? decisionRaw.reason : undefined,
          }
        : undefined,
    denied:
      deniedRaw != null
        ? {
            reason: typeof deniedRaw.reason === "string" ? deniedRaw.reason : undefined,
            requiredPermission:
              typeof deniedRaw.requiredPermission === "string"
                ? deniedRaw.requiredPermission
                : undefined,
          }
        : undefined,
    alternatives: Array.isArray(r.alternatives)
      ? r.alternatives.map((x) => String(x)).filter(Boolean)
      : undefined,
  };
}

export async function loadWorkforceUi(state: AppViewState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.workforceLoading = true;
  state.workforceError = null;
  try {
    const [configResult, insightsPayload] = await Promise.all([
      state.client.request<{ config: Record<string, unknown> }>("config.get", {}),
      state.client.request<WorkforceInsightsState>("workforce.insights", {}).catch(() => null),
    ]);

    if (insightsPayload && typeof insightsPayload === "object" && "stats" in insightsPayload) {
      state.workforceInsights = insightsPayload;
      maybeNotifyBudgetAlerts(insightsPayload.budgetAlerts ?? []);
    } else {
      state.workforceInsights = null;
    }

    const workforceConfig = configResult.config?.workforce as WorkforceConfig | undefined;
    state.workforceConfig = workforceConfig ?? null;

    const agentsList = asArray(
      configResult.config?.agents && (configResult.config.agents as Record<string, unknown>).list,
    );
    state.workforceAgents = extractWorkforceAgents(agentsList);
    state.workforceRoles = extractWorkforceRoles(workforceConfig);
    state.workforceUnits = extractWorkforceUnits(workforceConfig);
    state.workforceBindings = extractWorkforceBindings(workforceConfig);

    if (!state.workforceSelectedAgentId && state.workforceAgents.length > 0) {
      state.workforceSelectedAgentId = state.workforceAgents[0].agentId;
    }

    await loadFleetStatus(state);
  } catch (err) {
    state.workforceError = String(err);
  } finally {
    state.workforceLoading = false;
  }
}

export async function simulateWorkforceRouteUi(
  state: AppViewState,
  input: {
    channelId: string;
    messageType: "dm" | "group" | "channel" | "webhook";
    content?: string;
    requiredSkills?: string[];
  },
): Promise<void> {
  if (!state.client || !state.connected || state.workforceSimulateBusy) {
    return;
  }
  state.workforceSimulateBusy = true;
  state.workforceSimulateError = null;
  state.workforceSimulateResult = null;
  try {
    const payload = await state.client.request<{ result: unknown }>("workforce.route.simulate", {
      channelId: input.channelId.trim(),
      messageType: input.messageType,
      ...(input.content?.trim() ? { content: input.content.trim() } : {}),
      ...(input.requiredSkills && input.requiredSkills.length > 0
        ? { requiredSkills: input.requiredSkills }
        : {}),
    });
    state.workforceSimulateResult = normalizeSimulatePayload(payload.result);
  } catch (err) {
    state.workforceSimulateError = String(err);
  } finally {
    state.workforceSimulateBusy = false;
  }
}

export function selectWorkforceAgentUi(state: AppViewState, agentId: string | null): void {
  state.workforceSelectedAgentId = agentId;
}

export function setWorkforceViewUi(
  state: AppViewState,
  view: AppViewState["workforceSelectedView"],
): void {
  state.workforceSelectedView = view;
}

type WorkforceRoleCreateInput = {
  roleId: string;
  name: string;
  description?: string;
  skills: string[];
  priority?: number;
  maxSessions?: number;
  /**
   * The model this role runs on.
   *
   * JAISIU-4515 §4 — a model belongs to the ROLE, so every instance of
   * the role uses the same one. Leaving it unset makes the role inherit
   * the platform's default model.
   */
  model?: string;
};

type WorkforceUnitCreateInput = {
  unitId: string;
  name: string;
  parentUnitId: string | null;
  supervisorId: string;
  type: "division" | "department" | "team" | "project";
  tags?: string[];
};

type WorkforceBindingCreateInput = {
  bindingId: string;
  agentId: string;
  channels?: string[];
  keywords?: string[];
  requiredSkills?: string[];
  units?: string[];
  roles?: string[];
  messageTypes?: string[];
  priority: number;
  fallback: boolean;
};

export async function updateWorkforceAgentAssignmentUi(
  state: AppViewState,
  agentId: string,
  assignment: { roleId?: string; unitId?: string },
): Promise<void> {
  await mutateWorkforceConfigUi(state, (config) => {
    const list = asArray(config.agents && asRecord(config.agents).list);
    const index = list.findIndex((entry) => String(entry.id ?? "") === agentId);
    if (index < 0) {
      throw new Error(`Agent not found: ${agentId}`);
    }
    list[index] = {
      ...list[index],
      ...(assignment.roleId ? { roleId: assignment.roleId } : { roleId: undefined }),
      ...(assignment.unitId ? { unitId: assignment.unitId } : { unitId: undefined }),
    };
    config.agents = {
      ...asRecord(config.agents),
      list,
    };
  });
}

export async function addWorkforceRoleUi(
  state: AppViewState,
  role: WorkforceRoleCreateInput,
): Promise<void> {
  await mutateWorkforceConfigUi(state, (config) => {
    const workforce = ensureWorkforceConfig(config);
    const roles = Array.isArray(workforce.roles) ? [...workforce.roles] : [];
    if (roles.some((entry) => entry.roleId === role.roleId)) {
      throw new Error(`Role already exists: ${role.roleId}`);
    }
    roles.push({
      roleId: role.roleId,
      name: role.name,
      description: role.description || undefined,
      skills: role.skills,
      priority: role.priority ?? 1,
      maxSessions: role.maxSessions,
      ...(role.model?.trim() ? { model: role.model.trim() } : {}),
    });
    workforce.roles = roles;
    config.workforce = workforce;
  });
}

export async function addWorkforceUnitUi(
  state: AppViewState,
  unit: WorkforceUnitCreateInput,
): Promise<void> {
  await mutateWorkforceConfigUi(state, (config) => {
    const workforce = ensureWorkforceConfig(config);
    const units = Array.isArray(workforce.units) ? [...workforce.units] : [];
    if (units.some((entry) => entry.unitId === unit.unitId)) {
      throw new Error(`Unit already exists: ${unit.unitId}`);
    }
    units.push({
      unitId: unit.unitId,
      name: unit.name,
      parentUnitId: unit.parentUnitId,
      supervisorId: unit.supervisorId,
      type: unit.type,
      tags: unit.tags && unit.tags.length > 0 ? unit.tags : undefined,
    });
    workforce.units = units;
    config.workforce = workforce;
  });
}

export async function addWorkforceBindingUi(
  state: AppViewState,
  binding: WorkforceBindingCreateInput,
): Promise<void> {
  await mutateWorkforceConfigUi(state, (config) => {
    const workforce = ensureWorkforceConfig(config);
    const bindings = Array.isArray(workforce.bindings) ? [...workforce.bindings] : [];
    if (bindings.some((entry) => entry.bindingId === binding.bindingId)) {
      throw new Error(`Binding already exists: ${binding.bindingId}`);
    }
    bindings.push({
      bindingId: binding.bindingId,
      agentId: binding.agentId,
      match: {
        ...(binding.channels && binding.channels.length > 0 ? { channels: binding.channels } : {}),
        ...(binding.keywords && binding.keywords.length > 0 ? { keywords: binding.keywords } : {}),
        ...(binding.requiredSkills && binding.requiredSkills.length > 0
          ? { requiredSkills: binding.requiredSkills }
          : {}),
        ...(binding.units && binding.units.length > 0 ? { units: binding.units } : {}),
        ...(binding.roles && binding.roles.length > 0 ? { roles: binding.roles } : {}),
        ...(binding.messageTypes && binding.messageTypes.length > 0
          ? { messageTypes: binding.messageTypes }
          : {}),
      },
      priority: binding.priority,
      fallback: binding.fallback,
    });
    workforce.bindings = bindings;
    config.workforce = workforce;
  });
}

async function mutateWorkforceConfigUi(
  state: AppViewState,
  mutate: (config: Record<string, unknown>) => void,
): Promise<void> {
  if (!state.client || !state.connected || state.workforceSaving) {
    return;
  }
  state.workforceSaving = true;
  state.workforceError = null;
  try {
    const snapshot = await state.client.request<{
      hash?: string;
      config?: Record<string, unknown>;
    }>("config.get", {});
    const baseHash = snapshot.hash;
    const currentConfig = cloneConfig(snapshot.config);
    if (!baseHash || !currentConfig) {
      throw new Error("Config hash missing; reload and retry.");
    }
    mutate(currentConfig);
    const raw = serializeConfigForm(currentConfig);
    await state.client.request("config.set", {
      raw,
      baseHash,
    });
    await loadWorkforceUi(state);
  } catch (err) {
    state.workforceError = String(err);
  } finally {
    state.workforceSaving = false;
  }
}

type MutableWorkforceConfig = Record<string, unknown> & {
  enabled: boolean;
  roles?: unknown;
  units?: unknown;
  bindings?: unknown;
};

function ensureWorkforceConfig(config: Record<string, unknown>): MutableWorkforceConfig {
  const workforce = asRecord(config.workforce);
  return {
    ...workforce,
    enabled: workforce.enabled !== false,
  } as MutableWorkforceConfig;
}

function cloneConfig(config?: Record<string, unknown>) {
  if (!config) {
    return null;
  }
  return JSON.parse(JSON.stringify(config)) as Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) => asRecord(entry)).filter((entry) => Object.keys(entry).length > 0);
}

function extractWorkforceAgents(agentsList: Record<string, unknown>[]): WorkforceAgentRow[] {
  if (!agentsList.length) {
    return [];
  }
  return agentsList.map((agent) => {
    const employee = asRecord(agent.employee);
    return {
      agentId: String(agent.id ?? ""),
      employeeId: String(employee.employeeId ?? agent.id ?? ""),
      name: String(agent.name ?? employee.name ?? agent.id ?? ""),
      email: String(employee.email ?? `${String(agent.id ?? "agent")}@workforce.local`),
      department: (employee.department as string | undefined) ?? undefined,
      role: (employee.role as string | undefined) ?? undefined,
      status: (employee.status as WorkforceAgentRow["status"]) ?? "active",
      unitId: (agent.unitId as string | undefined) ?? undefined,
      roleId: (agent.roleId as string | undefined) ?? undefined,
      skills: Array.isArray(agent.skills)
        ? agent.skills.map((entry) => String(entry)).filter(Boolean)
        : undefined,
      location: (employee.location as string | undefined) ?? undefined,
      currentSpend: typeof agent.currentSpend === "number" ? agent.currentSpend : undefined,
      maxBudget: typeof agent.maxBudget === "number" ? agent.maxBudget : undefined,
    };
  });
}

/**
 * JAISIU-4515 §4 — reduce a role's model to a single label.
 *
 * The config accepts either a bare `provider/model` string or a
 * `{ primary, fallbacks }` object. The matrix shows one line, so the
 * primary is what is displayed; a role with fallbacks shows the primary
 * and the count of fallbacks.
 */
function normalizeRoleModelLabel(raw: unknown): string | undefined {
  if (typeof raw === "string") {
    return raw.trim() || undefined;
  }
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const entry = raw as { primary?: unknown; fallbacks?: unknown };
  const primary = typeof entry.primary === "string" ? entry.primary.trim() : "";
  const fallbacks = Array.isArray(entry.fallbacks)
    ? entry.fallbacks.filter((f): f is string => typeof f === "string" && f.trim().length > 0)
    : [];
  if (!primary) {
    return undefined;
  }
  return fallbacks.length > 0
    ? `${primary} (+${fallbacks.length} fallback${fallbacks.length === 1 ? "" : "s"})`
    : primary;
}

function extractWorkforceRoles(config?: WorkforceConfig): WorkforceRoleRow[] {
  if (!config?.roles) {
    return [
      {
        roleId: "general",
        name: "General Support",
        description: "Handles general inquiries and routes to specialists",
        skills: ["general-inquiry", "routing", "escalation"],
        priority: 1,
      },
      {
        roleId: "sales",
        name: "Sales Representative",
        description: "Handles sales inquiries and lead qualification",
        skills: ["sales", "lead-qualification", "pricing", "demos"],
        priority: 5,
      },
      {
        roleId: "support",
        name: "Customer Support",
        description: "Handles customer support tickets",
        skills: ["support", "troubleshooting", "refunds", "account-help"],
        priority: 4,
      },
      {
        roleId: "technical",
        name: "Technical Support",
        description: "Handles technical issues and integrations",
        skills: ["technical", "debugging", "api", "integrations", "code-review"],
        priority: 6,
      },
    ];
  }
  return config.roles.map((role) => ({
    roleId: role.roleId,
    name: role.name,
    description: role.description,
    skills: role.skills,
    priority: role.priority ?? 1,
    maxSessions: role.maxSessions,
    // JAISIU-4515 §4 — the role carries the model.
    model: normalizeRoleModelLabel(role.model),
  }));
}

function extractWorkforceUnits(config?: WorkforceConfig): WorkforceUnitRow[] {
  if (!config?.units) {
    return [
      {
        unitId: "executive",
        name: "Executive",
        parentUnitId: null,
        supervisorId: "",
        type: "division",
        tags: ["leadership"],
      },
      {
        unitId: "operations",
        name: "Operations",
        parentUnitId: "executive",
        supervisorId: "",
        type: "department",
        tags: ["core"],
      },
      {
        unitId: "engineering",
        name: "Engineering",
        parentUnitId: "executive",
        supervisorId: "",
        type: "department",
        tags: ["product"],
      },
    ];
  }
  return config.units.map((unit) => ({
    unitId: unit.unitId,
    name: unit.name,
    parentUnitId: unit.parentUnitId,
    supervisorId: unit.supervisorId,
    type: unit.type,
    tags: unit.tags,
  }));
}

function extractWorkforceBindings(config?: WorkforceConfig): WorkforceBindingRow[] {
  if (!config?.bindings) {
    return [];
  }
  return config.bindings.map((binding) => ({
    bindingId: binding.bindingId,
    agentId: binding.agentId,
    match: {
      channels: binding.match.channels,
      keywords: binding.match.keywords,
      requiredSkills: binding.match.requiredSkills,
      units: binding.match.units,
      roles: binding.match.roles,
      messageTypes: binding.match.messageTypes,
    },
    priority: binding.priority,
    fallback: binding.fallback,
  }));
}

export async function loadFleetStatus(
  state: AppViewState,
  opts?: { quiet?: boolean },
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const quiet = opts?.quiet === true;
  if (!quiet) {
    state.fleetLoading = true;
    state.fleetError = null;
  }
  try {
    const result = await state.client.request<{
      loads: Array<{ agentId: string; activeSessions: number; isOverloaded: boolean }>;
      stats: {
        totalActiveSessions: number;
        uniqueAgents: number;
        overloadedAgents: number;
        eventHistorySize: number;
      };
      activeAgents: Array<{
        agentId: string;
        sessionKey: string;
        label?: string;
        status: string;
        message?: string;
        lastUpdate: number;
        spawnedAt: number;
      }>;
      recentEvents: Array<{
        type: "agent:spawned" | "agent:status" | "agent:completed" | "agent:error";
        agentId: string;
        sessionKey: string;
        label?: string;
        status?: string;
        message?: string;
        error?: string;
        timestamp: number;
        correlationId?: string;
      }>;
    }>("workforce.fleet.status", {});

    state.fleetLoads = result.loads ?? [];
    state.fleetStats = result.stats ?? null;
    state.fleetActiveAgents = result.activeAgents ?? [];
    state.fleetEventHistory = result.recentEvents ?? [];
  } catch (err) {
    if (!quiet) {
      state.fleetError = String(err);
    }
  } finally {
    if (!quiet) {
      state.fleetLoading = false;
    }
  }
}

export async function loadAgentLoad(
  state: AppViewState,
  agentId: string,
): Promise<{
  loads: Array<{
    agentId: string;
    activeSessions: number;
    isOverloaded: boolean;
    sessionKey: string;
    status: string;
    lastUpdate: number;
  }>;
  eventHistory: Array<{
    type: "agent:spawned" | "agent:status" | "agent:completed" | "agent:error";
    agentId: string;
    sessionKey: string;
    label?: string;
    status?: string;
    message?: string;
    error?: string;
    timestamp: number;
  }>;
} | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  try {
    const result = await state.client.request<{
      loads: Array<{
        agentId: string;
        activeSessions: number;
        isOverloaded: boolean;
        sessionKey: string;
        status: string;
        lastUpdate: number;
      }>;
      eventHistory: Array<{
        type: "agent:spawned" | "agent:status" | "agent:completed" | "agent:error";
        agentId: string;
        sessionKey: string;
        label?: string;
        status?: string;
        message?: string;
        error?: string;
        timestamp: number;
      }>;
    }>("workforce.agent.load", { agentId });
    return result;
  } catch {
    return null;
  }
}
